// frontend/src/components/mcbim/ComponentsMenu.tsx
import React, { useMemo } from "react";
import {
  Box,
  Button,
  Divider,
  Flex,
  Menu,
  NavLink,
  ScrollArea,
  Stack,
  Text,
  Group,
} from "@mantine/core";
import { FiChevronDown } from "react-icons/fi";
import { MdViewModule } from "react-icons/md";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
  IMcBimItemColorMode,
  setMcBimItemColorMode,
  setMcBimItemsFilterModeInRedux,
} from "../../designSlice";
import { IfcClassesMenuContent } from "./IfcClassesMenuContent";
import { DisciplinesMenuContent } from "./DisciplinesMenuContent";
import { BlobMediaPreview } from "../../../Releasenotes/BlobMedia";
import { MediaAssetViewer } from "../../../MediaAssets/MediaAssetViewer";
import { MEDIA_ASSET_KEYS } from "../../../../../packages/shared/dist/esm/schemas";

export type IMcBimItemsFilterMode = "INSTANCE_CLASS" | "DISCIPLINE";

export const ComponentsMenu: React.FC = () => {
  const dispatch = useAppDispatch();

  const mcbimItemsFilterMode = useAppSelector(
    (s) => s.design.mcbimItemsFilterMode
  ) as IMcBimItemsFilterMode;

  const mcbimItemColorMode = useAppSelector(
    (s) => s.design.mcbimItemColorMode
  ) as IMcBimItemColorMode;

  const filterModes = [
    { value: "INSTANCE_CLASS" as const, label: "IFC-klasser" },
    { value: "DISCIPLINE" as const, label: "Disiplin" },
  ];

  const colorModes = [
    { value: "MULTIPLACER_COLORS" as const, label: "IfcKlasfarger" },
    { value: "MMI" as const, label: "MMI" },
    { value: "DISCIPLINE_COLORS" as const, label: "Disiplin" },
    { value: "PRIMARY_SYSTEM" as const, label: "System" },
  ];

  const activeRoomColoringLabel = useMemo(() => {
    return filterModes.find((m) => m.value === mcbimItemsFilterMode)?.label ?? null;
  }, [mcbimItemsFilterMode]);

  const handleSelectFilterMode = (value: IMcBimItemsFilterMode) => {
    dispatch(setMcBimItemsFilterModeInRedux(value));
  };

  const handleSelectColorMode = (value: IMcBimItemColorMode) => {
    dispatch(setMcBimItemColorMode(value));
  };

  return (
    <Group gap="xs" align="center" wrap="nowrap">
      <MediaAssetViewer assetKey={MEDIA_ASSET_KEYS.COMPONENT} mode="ICON" />
      <Menu
        withinPortal
        position="bottom-start"
        shadow="xl"
        width={740}
        closeOnItemClick={false}
        styles={{ dropdown: { backgroundColor: "rgba(255, 255, 255, 0.95)" } }}
      >
        <Menu.Target>
          <Button
            size="sm"
            variant="subtle"
            leftSection={<MdViewModule size={16} />}
            rightSection={<FiChevronDown size={14} />}
            w={280}
            justify="space-between"
            styles={{ label: { width: "100%" } }}
          >
            <Text truncate fw={500}>
              Komponenter
            </Text>
          </Button>
        </Menu.Target>

        <Menu.Dropdown>
          {/* Column headers */}
          <Box style={{ display: "flex" }} p="md" pb={8}>
            <Box style={{ flex: "0 0 240px", paddingLeft: 12 }}>
              <Text fw={700} size="sm">
                Innstillinger
              </Text>
            </Box>
            <Box style={{ flex: 1, paddingLeft: 12 }}>
              <Text fw={700} size="sm">
                {activeRoomColoringLabel ? `Komponenter etter ${activeRoomColoringLabel}` : "Komponenter"}
              </Text>
            </Box>
          </Box>

          <Divider />

          {/* Two-column layout */}
          <Box style={{ display: "flex", height: 500 }}>
            {/* Left: Settings */}
            <Box style={{ flex: "0 0 240px" }}>
              <ScrollArea h="100%" type="hover">
                <Stack gap={0}>
                  <Text pl={12} py={8} size="xs" fw={700} c="dimmed">
                    Filtertype
                  </Text>
                  {filterModes.map((m) => {
                    const isActive = m.value === mcbimItemsFilterMode;

                    return (
                      <NavLink
                        key={m.value}
                        active={isActive}
                        label={
                          <Text truncate fw={isActive ? 600 : 400}>
                            {m.label}
                          </Text>
                        }
                        onClick={() => handleSelectFilterMode(m.value)}
                        variant="subtle"
                        styles={{
                          root: {
                            borderRadius: 0,
                            padding: "10px 12px",
                          },
                          label: {
                            width: "100%",
                          },
                        }}
                      />
                    );
                  })}
                  <Divider my="md" />
                  <Text pl={12} py={8} size="xs" fw={700} c="dimmed">
                    Fargemodus
                  </Text>
                  {colorModes.map((m) => {
                    const isActive = m.value === mcbimItemColorMode;

                    return (
                      <NavLink
                        key={m.value}
                        active={isActive}
                        label={
                          <Text truncate fw={isActive ? 600 : 400}>
                            {m.label}
                          </Text>
                        }
                        onClick={() => handleSelectColorMode(m.value)}
                        variant="subtle"
                        styles={{
                          root: {
                            borderRadius: 0,
                            padding: "10px 12px",
                          },
                          label: {
                            width: "100%",
                          },
                        }}
                      />
                    );
                  })}
                </Stack>
              </ScrollArea>
            </Box>

            {/* Right: Content */}
            <Box style={{ flex: 1 }}>
              <ScrollArea h="100%" type="hover">
                <Stack gap={0} p="md">
                  {mcbimItemsFilterMode === "DISCIPLINE" ? (
                    <DisciplinesMenuContent />
                  ) : (
                    <IfcClassesMenuContent />
                  )}
                </Stack>
              </ScrollArea>
            </Box>
          </Box>
        </Menu.Dropdown>
      </Menu>
    </Group>
  );
};
